import AppIconButton from "../form/AppIconButton";
import Autocomplete from "@mui/material/Autocomplete";
import { Box, Chip, Collapse, Stack, Typography } from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import PinOutlinedIcon from "@mui/icons-material/PinOutlined";
import FunctionsOutlinedIcon from "@mui/icons-material/FunctionsOutlined";
import { useTranslation } from "react-i18next";
import {
  ColumnFormatCustomFields,
  ColumnFormatSelectControl,
} from "../ColumnFormatSelect";
import { AppCheckbox, AppTextField, AppToggleField } from "../form";
import AppColorField from "../form/AppColorField";
import {
  columnHasConstant,
  columnHasFormula,
  columnUsesCompositeSources,
  columnValueSource,
  headerToColumnKey,
  patchFormulaColumnField,
  fieldOptionsToColumn,
  formatCompositeColumnFieldLabel,
  formatTableResultFieldLabel,
  normalizeFieldPickerSelection,
  resolveColumnFieldPickerValue,
  tableFieldPickerOptionsEqual,
  type ColumnBarFill,
  type ColumnValueSource,
  type TableColumnMapping,
} from "../../utils/tableMappingBuilder";
import type { TableResultFieldOption } from "../../utils/openSearchTableAnalysis";
import FormulaTokenPicker from "./FormulaTokenPicker";
import { TABLE_CELL_INPUT_SX, TABLE_ROW_GRID, tableCellFieldProps } from "./tableCellStyles";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import { useState } from "react";

type Props = {
  columns: TableColumnMapping[];
  fieldOptions: TableResultFieldOption[];
  disabled?: boolean;
  onChange: (columns: TableColumnMapping[]) => void;
};

function patchValueSource(
  column: TableColumnMapping,
  source: ColumnValueSource,
  index: number
): Partial<TableColumnMapping> {
  if (source === "field") {
    return { constant: undefined, formula: undefined };
  }
  const rowKey = headerToColumnKey(column.header, index);
  if (source === "constant") {
    return {
      formula: undefined,
      constant: column.constant ?? "",
      field: rowKey,
      path: "",
      sources: undefined,
    };
  }
  return {
    constant: undefined,
    formula: column.formula ?? "",
    field: patchFormulaColumnField(column, index),
    path: "",
    sources: undefined,
  };
}

function patchBarFill(column: TableColumnMapping, patch: Partial<ColumnBarFill>): TableColumnMapping {
  const next = { ...(column.barFill ?? {}), ...patch };
  const hasConfig =
    next.mode || next.color || next.showValue != null || next.min != null || next.max != null;
  return { ...column, barFill: hasConfig ? next : undefined };
}

export default function TableColumnsEditor({ columns, fieldOptions, disabled, onChange }: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.tableColumnsEditor;
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const updateColumn = (index: number, patch: Partial<TableColumnMapping>) => {
    onChange(columns.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  const removeColumn = (index: number) => {
    onChange(columns.filter((_, i) => i !== index));
  };

  const toggleExpanded = (index: number) => {
    setExpanded((current) => ({ ...current, [index]: !current[index] }));
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.headerRow}>
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          {t("visualEditor.columnField")}
        </Typography>
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          {t("visualEditor.columnHeader")}
        </Typography>
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          {t("columnFormat.typeLabel")}
        </Typography>
        <Box />
      </Box>

      {columns.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={styles.emptyState}>
          {t("visualEditor.noColumnsYet")}
        </Typography>
      )}

      {columns.map((col, index) => {
        const hasCustomFormat = col.format?.preset === "custom";
        const selectedOptions = resolveColumnFieldPickerValue(col, fieldOptions);
        const isComposite = columnUsesCompositeSources(col);
        const fieldHelper = isComposite ? t("visualEditor.columnFieldCompositeHint") : undefined;
        const valueSource = columnValueSource(col);
        const showFieldPicker = valueSource === "field";
        const isAdvancedOpen = expanded[index] ?? (columnHasConstant(col) || columnHasFormula(col) || col.subtotal || Boolean(col.barFill));

        return (
          <Box key={`column-${index}`} sx={styles.dataRow(index < columns.length - 1)}>
            <Box sx={TABLE_ROW_GRID}>
              <Box sx={styles.fieldCol}>
                <Typography variant="caption" color="text.secondary" sx={styles.mobileLabel}>
                  {t("visualEditor.columnField")}
                </Typography>
                {showFieldPicker ? (
                  <Autocomplete<TableResultFieldOption, true, false, true>
                    multiple
                    freeSolo
                    size="small"
                    fullWidth
                    options={fieldOptions}
                    value={selectedOptions}
                    onChange={(_, opts) => {
                      const normalized = normalizeFieldPickerSelection(opts);
                      if (!normalized.length) {
                        updateColumn(index, {
                          ...fieldOptionsToColumn([]),
                          header: col.header,
                          format: col.format,
                          constant: col.constant,
                          formula: col.formula,
                          subtotal: col.subtotal,
                          barFill: col.barFill,
                        });
                        return;
                      }
                      const next = fieldOptionsToColumn(normalized);
                      updateColumn(index, {
                        ...next,
                        sources: columnUsesCompositeSources(next) ? next.sources : undefined,
                        format: col.format,
                        header: col.header?.trim() ? col.header : next.header,
                        constant: undefined,
                        formula: col.formula,
                        subtotal: col.subtotal,
                        barFill: col.barFill,
                      });
                    }}
                    getOptionLabel={(o) =>
                      typeof o === "string" ? o : formatTableResultFieldLabel(o)
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, tagIndex) => (
                        <Chip
                          {...getTagProps({ index: tagIndex })}
                          key={`${typeof option === "string" ? option : `${option.group || option.field}:${option.path}`}:${tagIndex}`}
                          label={
                            typeof option === "string"
                              ? option
                              : formatTableResultFieldLabel(option)
                          }
                          size="small"
                        />
                      ))
                    }
                    renderOption={(props, o) => (
                      <Box component="li" {...props} key={`${o.group || o.field}:${o.path}`}>
                        <Typography variant="body2" component="span" fontWeight={600}>
                          {formatTableResultFieldLabel(o)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={styles.optionTypeCaption}>
                          {o.type}
                        </Typography>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <AppTextField
                        {...params}
                        {...tableCellFieldProps}
                        label={t("visualEditor.columnField")}
                        placeholder={t("visualEditor.columnFieldPlaceholder")}
                        sx={styles.fieldAutocompleteInput}
                      />
                    )}
                    isOptionEqualToValue={(a, b) => {
                      if (typeof a === "string" || typeof b === "string") {
                        return typeof a === "string" && typeof b === "string" && a === b;
                      }
                      return tableFieldPickerOptionsEqual(a, b);
                    }}
                    filterSelectedOptions
                    disabled={disabled}
                    disableCloseOnSelect
                    sx={styles.fieldAutocomplete}
                  />
                ) : valueSource === "constant" ? (
                  <AppTextField
                    {...tableCellFieldProps}
                    label={t("visualEditor.columnConstant")}
                    placeholder={t("visualEditor.columnConstantPlaceholder")}
                    value={col.constant ?? ""}
                    onChange={(e) => updateColumn(index, { constant: e.target.value })}
                    disabled={disabled}
                    sx={TABLE_CELL_INPUT_SX}
                  />
                ) : (
                  <AppTextField
                    {...tableCellFieldProps}
                    label={t("visualEditor.columnFormula")}
                    placeholder={t("visualEditor.columnFormulaPlaceholder")}
                    value={col.formula ?? ""}
                    onChange={(e) => updateColumn(index, { formula: e.target.value })}
                    disabled={disabled}
                    sx={TABLE_CELL_INPUT_SX}
                  />
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={styles.fieldHelperText}
                  aria-hidden={!fieldHelper && valueSource === "field"}
                >
                  {valueSource === "formula"
                    ? t("visualEditor.columnFormulaHint")
                    : fieldHelper || "\u00a0"}
                </Typography>
                {isComposite && selectedOptions.length > 1 && (
                  <Typography variant="caption" color="text.secondary" sx={styles.compositeFieldHint}>
                    {formatCompositeColumnFieldLabel(selectedOptions)}
                  </Typography>
                )}
              </Box>

              <Box sx={styles.fieldCol}>
                <Typography variant="caption" color="text.secondary" sx={styles.mobileLabel}>
                  {t("visualEditor.columnHeader")}
                </Typography>
                <AppTextField
                  {...tableCellFieldProps}
                  label={t("visualEditor.columnHeader")}
                  placeholder={t("visualEditor.columnHeader")}
                  value={col.header}
                  onChange={(e) => updateColumn(index, { header: e.target.value })}
                  onBlur={() => {
                    if (valueSource === "field") return;
                    const nextField = headerToColumnKey(col.header, index);
                    if (nextField === col.field) return;
                    updateColumn(index, { field: nextField });
                  }}
                  disabled={disabled}
                  sx={TABLE_CELL_INPUT_SX}
                />
              </Box>

              <Box sx={styles.fieldCol}>
                <Typography variant="caption" color="text.secondary" sx={styles.mobileLabel}>
                  {t("columnFormat.typeLabel")}
                </Typography>
                <ColumnFormatSelectControl
                  value={col.format}
                  disabled={disabled}
                  onChange={(format) => updateColumn(index, { format })}
                />
              </Box>

              <Box sx={styles.deleteButtonCol}>
                <AppIconButton
                  size="small"
                  onClick={() => toggleExpanded(index)}
                  disabled={disabled}
                  aria-label={t("visualEditor.columnAdvanced")}
                  sx={{
                    transform: isAdvancedOpen ? "rotate(180deg)" : "none",
                    transition: "transform 0.2s",
                  }}
                >
                  <ExpandMoreIcon fontSize="small" />
                </AppIconButton>
                <AppIconButton
                  size="small"
                  color="error"
                  confirmDelete={{ itemName: col.header || col.field || String(index + 1) }}
                  onClick={() => removeColumn(index)}
                  disabled={disabled}
                  aria-label={t("common.delete")}
                >
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </AppIconButton>
              </Box>
            </Box>

            {hasCustomFormat && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={styles.customFormatStack}>
                <ColumnFormatCustomFields
                  value={col.format}
                  disabled={disabled}
                  onChange={(format) => updateColumn(index, { format })}
                />
              </Stack>
            )}

            <Collapse in={isAdvancedOpen}>
              <Stack spacing={1.25} sx={styles.advancedStack}>
                <AppToggleField
                  hideLabel
                  value={valueSource}
                  disabled={disabled}
                  options={[
                    {
                      value: "field",
                      label: t("visualEditor.columnSourceField"),
                      icon: <TableRowsOutlinedIcon fontSize="small" />,
                    },
                    {
                      value: "constant",
                      label: t("visualEditor.columnSourceConstant"),
                      icon: <PinOutlinedIcon fontSize="small" />,
                    },
                    {
                      value: "formula",
                      label: t("visualEditor.columnSourceFormula"),
                      icon: <FunctionsOutlinedIcon fontSize="small" />,
                    },
                  ]}
                  onChange={(next) => updateColumn(index, patchValueSource(col, next as ColumnValueSource, index))}
                />

                {valueSource === "formula" && (
                  <FormulaTokenPicker
                    columns={columns}
                    disabled={disabled}
                    onApply={(token) => {
                      const current = col.formula ?? "";
                      const spacer =
                        current && !current.endsWith(" ") && !current.endsWith("(") ? " " : "";
                      updateColumn(index, { formula: `${current}${spacer}${token}` });
                    }}
                  />
                )}

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                  <AppCheckbox
                    label={t("visualEditor.columnSubtotal")}
                    checked={Boolean(col.subtotal)}
                    disabled={disabled}
                    onChange={(_, checked) => updateColumn(index, { subtotal: checked })}
                  />
                  <AppCheckbox
                    label={t("visualEditor.columnBarFillEnable")}
                    checked={Boolean(col.barFill)}
                    disabled={disabled}
                    onChange={(_, checked) =>
                      updateColumn(
                        index,
                        checked
                          ? patchBarFill(col, { mode: "positive", showValue: true })
                          : { barFill: undefined }
                      )
                    }
                  />
                </Stack>

                {col.barFill && (
                  <Stack spacing={1} sx={styles.barFillStack}>
                    <AppToggleField
                      label={t("visualEditor.columnBarFillMode")}
                      value={col.barFill.mode ?? "positive"}
                      disabled={disabled}
                      options={[
                        { value: "positive", label: t("visualEditor.columnBarFillPositive") },
                        { value: "negative", label: t("visualEditor.columnBarFillNegative") },
                        { value: "both", label: t("visualEditor.columnBarFillBoth") },
                      ]}
                      onChange={(mode) =>
                        updateColumn(index, patchBarFill(col, { mode: mode as ColumnBarFill["mode"] }))
                      }
                    />
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                      <AppColorField
                        compact
                        label={t("visualEditor.columnBarFillColor")}
                        value={col.barFill.color ?? "#5470c6"}
                        onChange={(color) => updateColumn(index, patchBarFill(col, { color }))}
                        disabled={disabled}
                      />
                      <AppCheckbox
                        label={t("visualEditor.columnBarFillShowValue")}
                        checked={col.barFill.showValue !== false}
                        disabled={disabled}
                        onChange={(_, checked) => updateColumn(index, patchBarFill(col, { showValue: checked }))}
                      />
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}
